import React, { useCallback, useMemo, useState } from 'react'
import { ArrowLeft, Sparkles, Trophy } from 'lucide-react'

const SIZE = 3
const TILES = [
  { id: 1, icon: '▶', label: 'Play' },
  { id: 2, icon: '🎤', label: 'Voz' },
  { id: 3, icon: '▦', label: 'Bloque' },
  { id: 4, icon: '✦', label: 'FX' },
  { id: 5, icon: '♪', label: 'Tarareo' },
  { id: 6, icon: '🧠', label: 'Brain' },
  { id: 7, icon: '⚡', label: 'Power' },
  { id: 8, icon: '✨', label: 'Cosmos' },
]

function solved() {
  return [...TILES.map((t) => t.id), null]
}

function indexToRowCol(i) {
  return { r: Math.floor(i / SIZE), c: i % SIZE }
}

function rowColToIndex(r, c) {
  return r * SIZE + c
}

function neighbors(emptyIdx) {
  const { r, c } = indexToRowCol(emptyIdx)
  const out = []
  if (r > 0) out.push(rowColToIndex(r - 1, c))
  if (r < SIZE - 1) out.push(rowColToIndex(r + 1, c))
  if (c > 0) out.push(rowColToIndex(r, c - 1))
  if (c < SIZE - 1) out.push(rowColToIndex(r, c + 1))
  return out
}

function shuffleBoard() {
  let board = solved()
  let empty = board.length - 1
  for (let i = 0; i < 120; i++) {
    const opts = neighbors(empty)
    const pick = opts[Math.floor(Math.random() * opts.length)]
    board[empty] = board[pick]
    board[pick] = null
    empty = pick
  }
  return board
}

function isWin(board) {
  const goal = solved()
  for (let i = 0; i < goal.length; i++) {
    if (board[i] !== goal[i]) return false
  }
  return true
}

export function ControlDockSlidePuzzle({ onBack, onComplete }) {
  const [board, setBoard] = useState(() => shuffleBoard())
  const [moves, setMoves] = useState(0)
  const [won, setWon] = useState(false)

  const emptyIdx = useMemo(() => board.indexOf(null), [board])

  const tileById = useMemo(() => Object.fromEntries(TILES.map((t) => [t.id, t])), [])

  const tap = useCallback(
    (idx) => {
      if (won) return
      if (!neighbors(emptyIdx).includes(idx)) return
      setBoard((prev) => {
        const next = [...prev]
        next[emptyIdx] = next[idx]
        next[idx] = null
        return next
      })
      setMoves((m) => m + 1)
    },
    [emptyIdx, won],
  )

  React.useEffect(() => {
    if (won) return
    if (isWin(board)) {
      setWon(true)
      onComplete?.(Math.max(40, 120 - moves * 2))
    }
  }, [board, moves, onComplete, won])

  const reset = () => {
    setBoard(shuffleBoard())
    setMoves(0)
    setWon(false)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-300 hover:text-white"
        >
          <ArrowLeft size={18} /> Volver
        </button>
        <span className="text-xs font-bold text-pink-300/90 uppercase tracking-wider">Cosmos Power Brain</span>
      </div>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-black text-white flex items-center justify-center gap-2">
          <Sparkles className="text-pink-400" size={22} />
          Puzzle del Dock
        </h2>
        <p className="text-sm text-slate-400 max-w-md mx-auto">
          Como el panel de Ritmorbit: un hueco vacío. Desliza los iconos hasta ordenarlos 1→8. Sal del laberinto mental.
        </p>
        <p className="text-xs text-slate-500">Movimientos: {moves}</p>
      </div>

      <div className="mx-auto max-w-[220px] rounded-2xl border border-pink-500/35 bg-[#120a14] p-2 shadow-[0_0_32px_rgba(255,107,157,.15)]">
        <div className="grid grid-cols-3 gap-1.5">
          {board.map((cell, idx) => {
            if (cell === null) {
              return <div key={`e-${idx}`} className="aspect-square rounded-xl border border-dashed border-pink-500/25 bg-black/30" />
            }
            const tile = tileById[cell]
            return (
              <button
                key={`${idx}-${cell}`}
                type="button"
                onClick={() => tap(idx)}
                className="aspect-square rounded-xl border border-pink-500/45 bg-gradient-to-b from-pink-500/15 to-black/50 text-2xl flex flex-col items-center justify-center gap-0.5 hover:border-pink-400 hover:shadow-[0_0_12px_rgba(255,107,157,.35)] transition-all active:scale-95"
              >
                <span>{tile.icon}</span>
                <span className="text-[9px] font-bold text-pink-200/80">{cell}</span>
              </button>
            )
          })}
        </div>
      </div>

      {won ? (
        <div className="text-center space-y-3 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/80 border border-emerald-500/40 text-emerald-300 text-sm font-bold">
            <Trophy size={18} /> ¡Saliste! Orden correcto en {moves} movimientos
          </div>
          <button
            type="button"
            onClick={reset}
            className="text-sm font-bold text-cyan-400 hover:underline"
          >
            Jugar otra vez
          </button>
        </div>
      ) : (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={reset}
            className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 text-sm font-bold hover:bg-slate-800"
          >
            Mezclar de nuevo
          </button>
        </div>
      )}
    </div>
  )
}
